import React, { useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { formatMeterRaw } from './dailyDataUtils'

export function SlotAnalysisSummary({ slot }) {
  if (!slot) {
    return <p className="text-sm text-gray-600">No FactSlotAnalysisDaily data for this trading day.</p>
  }

  return (
    <>
      <div className="mb-3 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded p-2">
        <strong>Jackpot types:</strong> Progressive = linked jackpots across machines
        {' · '}Jackpot win = total jackpot payouts
        {' · '}Handpays = manual payouts by staff
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="rounded-lg bg-purple-50 border border-purple-100 p-3">
          <div className="text-xs text-purple-600 uppercase font-medium">Cancel credit</div>
          <div className="text-xl font-bold text-purple-900 tabular-nums">
            {formatMeterRaw(slot.total_cancel_credit)}
          </div>
          <div className="text-xs text-purple-600 mt-1">{slot.egms_with_cancel_credit} EGMs</div>
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
          <div className="text-xs text-amber-600 uppercase font-medium">Jackpot wins</div>
          <div className="text-xl font-bold text-amber-900 tabular-nums">
            {formatMeterRaw(slot.total_jackpot)}
          </div>
          <div className="text-xs text-amber-600 mt-1">{slot.egms_with_jackpots} EGMs won</div>
        </div>
        <div className="rounded-lg bg-orange-50 border border-orange-100 p-3">
          <div className="text-xs text-orange-600 uppercase font-medium">Progressive</div>
          <div className="text-xl font-bold text-orange-900 tabular-nums">
            {formatMeterRaw(slot.total_progressive)}
          </div>
          <div className="text-xs text-orange-600 mt-1">Linked jackpots</div>
        </div>
        <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
          <div className="text-xs text-blue-600 uppercase font-medium">Handpays</div>
          <div className="text-xl font-bold text-blue-900 tabular-nums">
            {slot.total_handpays || 0}
          </div>
          <div className="text-xs text-blue-600 mt-1">Manual payouts</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-xs text-gray-500 uppercase">Turnover</div>
          <div className="text-lg font-semibold tabular-nums">{formatMeterRaw(slot.total_turnover)}</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-xs text-gray-500 uppercase">Net win</div>
          <div className="text-lg font-semibold tabular-nums">{formatMeterRaw(slot.total_net_win)}</div>
        </div>
        <div className="rounded-lg bg-purple-50 border border-purple-100 p-3">
          <div className="text-xs text-purple-600 uppercase font-medium">Machine payouts</div>
          <div className="text-lg font-bold text-purple-900 tabular-nums">
            {formatMeterRaw(slot.machine_payouts || 0)}
          </div>
          <div className="text-xs text-purple-600 mt-1">Processed − retained</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-xs text-gray-500 uppercase">EGMs in file</div>
          <div className="text-lg font-semibold tabular-nums">{slot.egm_count}</div>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        From FactSlotAnalysisDaily (daily EGM summary), shown as AUD.
        <span className="font-medium text-purple-700">
          {' '}Machine payouts may not include cage payouts or promotional vouchers.
        </span>
        {slot.egms_with_jackpots > 0 && (
          <span className="font-medium text-amber-700">
            {' '}{slot.egms_with_jackpots} machines had jackpot wins.
          </span>
        )}
      </p>
    </>
  )
}

export default function SlotAnalysisPanel({ slot }) {
  const [expanded, setExpanded] = useState(false)
  const hasData = slot != null

  return (
    <div className="bg-white shadow rounded-lg border-l-4 border-purple-500 p-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left"
        aria-expanded={expanded}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 min-w-0">
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            <span className="text-sm font-semibold text-gray-900">Slot analysis</span>
            {!hasData && (
              <span className="text-[10px] font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                No file
              </span>
            )}
          </span>
          <span className="text-[11px] text-gray-500">{expanded ? 'Hide' : 'Show'}</span>
        </span>
        {!expanded && hasData && (
          <span className="mt-2 grid grid-cols-4 gap-2 text-[11px]">
            <span className="rounded bg-gray-50 px-2 py-1">
              <span className="block text-gray-500 uppercase">Cancel cr.</span>
              <span className="font-semibold tabular-nums text-gray-900">{formatMeterRaw(slot.total_cancel_credit)}</span>
            </span>
            <span className="rounded bg-gray-50 px-2 py-1">
              <span className="block text-gray-500 uppercase">Jackpots</span>
              <span className="font-semibold tabular-nums text-gray-900">{formatMeterRaw(slot.total_jackpot)}</span>
            </span>
            <span className="rounded bg-gray-50 px-2 py-1">
              <span className="block text-gray-500 uppercase">Progressive</span>
              <span className="font-semibold tabular-nums text-gray-900">{formatMeterRaw(slot.total_progressive)}</span>
            </span>
            <span className="rounded bg-gray-50 px-2 py-1">
              <span className="block text-gray-500 uppercase">Handpays</span>
              <span className="font-semibold tabular-nums text-gray-900">{slot.total_handpays || 0}</span>
            </span>
          </span>
        )}
      </button>
      {expanded && (
        <div className="mt-3">
          <SlotAnalysisSummary slot={slot} />
        </div>
      )}
    </div>
  )
}
